// Sugalarm — activation guard, cycle 365.
//
// Runs on every authed launch and every resume. Two jobs:
//
//   1. SELF-HEAL, silently. If the OS has already granted notifications
//      but this install never armed native push (the local
//      `sugalarm.nativePush.enabled` flag is the only thing gating
//      `bootstrapNativePush`, and on a phone it is set solely by tapping
//      "Enable on this device"), arm it now and register. Permission is
//      already granted, so NO prompt appears and the user sees nothing —
//      the phone just becomes pageable. This is the path that fixes a
//      phone that has been silently unregistered for months.
//
//   2. REPORT, always. Post the capability facts and keep the server's
//      verdict so the prompt and the admin roster agree.
//
// Best-effort throughout: every failure is swallowed. This hook can never
// block a render, delay a reading, or influence alarm delivery.

import { useCallback, useEffect, useRef, useState } from "react";

import { reportActivation } from "../api/activation";
import {
  canSilentlyArm,
  collectActivationReport,
  type ActivationVerdict,
} from "../lib/activation";
import { getOrCreateClientId } from "../lib/auth";
import {
  bootstrapNativePush,
  isNativePushEnabledLocally,
  setNativePushEnabledLocally,
} from "../lib/nativePush";

/** Re-check cadence while the app stays open. Deliberately slow — this is
 *  a readiness audit, not a poll of anything time-sensitive. */
const RECHECK_INTERVAL_MS = 15 * 60 * 1000;

export interface ActivationGuardOptions {
  /** Null until signed in — the hook is inert without it. */
  token: string | null;
  appVersion: string;
  /** Threaded into `bootstrapNativePush` so a self-healed install wires
   *  the same deep-link handler a normal arm would. */
  onAlertId: (alertId: string) => void;
}

export function useActivationGuard({
  token,
  appVersion,
  onAlertId,
}: ActivationGuardOptions): {
  verdict: ActivationVerdict | null;
  /** Re-audit now — called after the user acts on the prompt so the card
   *  clears itself the moment the gap actually closes. */
  recheck: () => void;
} {
  const [verdict, setVerdict] = useState<ActivationVerdict | null>(null);
  const inFlight = useRef(false);
  const onAlertIdRef = useRef(onAlertId);
  onAlertIdRef.current = onAlertId;

  const run = useCallback(async () => {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    try {
      const clientId = getOrCreateClientId();
      let report = await collectActivationReport(clientId, appVersion);

      // ── Silent self-heal. Permission already granted + never armed
      //    locally ⇒ arm and register with zero user-visible effect.
      if (canSilentlyArm(report) && !isNativePushEnabledLocally()) {
        setNativePushEnabledLocally(true);
        try {
          await bootstrapNativePush({ onAlertId: onAlertIdRef.current });
          // Re-collect so the report reflects the arm we just did.
          report = await collectActivationReport(clientId, appVersion);
        } catch (err) {
          console.warn("[activation] silent arm failed", err);
        }
      }

      const r = await reportActivation(report);
      if (r.ok) setVerdict(r.data);
    } catch (err) {
      console.warn("[activation] report failed", err);
    } finally {
      inFlight.current = false;
    }
  }, [token, appVersion]);

  useEffect(() => {
    if (!token) {
      setVerdict(null);
      return;
    }
    void run();

    const onVisible = () => {
      if (document.visibilityState === "visible") void run();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const id = window.setInterval(() => void run(), RECHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearInterval(id);
    };
  }, [token, run]);

  const recheck = useCallback(() => {
    void run();
  }, [run]);

  return { verdict, recheck };
}
